// App v1.1 - Dark Mode Support
import { useEffect } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import ErrorBoundary from "@/components/ErrorBoundary";
import LocationAwareErrorBoundary from "@/components/LocationAwareErrorBoundary";
import { AppLayout } from "@/components/AppLayout";
import { queryClient } from "@/config/query-client";
import { ThemeProvider } from "@/components/ThemeProvider";
import Home from "./pages/Home";
import { CampaignPage } from "./pages/Campaign";
import { CampaignChoicePage } from "./pages/CampaignChoice";
import { AICampaignChatPage } from "./pages/AICampaignChat";
import AICampaignCreator from "./pages/AICampaignCreator";
import Contact from "./pages/Contact";
import NotFound from "./pages/NotFound";
import PaymentCallback from "./pages/PaymentCallback";
import Partners from "./pages/Partners";
import PartnerDetail from "./pages/PartnerDetail";
import WhoWeAre from "./pages/WhoWeAre";
import FAQs from "./pages/FAQs";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import ReturnPolicy from "./pages/ReturnPolicy";
import Influencers from "./pages/Influencers";
import InfluencerBooking from "./pages/InfluencerBooking";
import InfluencerDetail from "./pages/InfluencerDetail";
import BookInfluencer from "./pages/InfluencerBooking/BookInfluencer";
import InfluencerSignUp from "./pages/InfluencerSignUp";
import InfluencerDashboard from "./pages/InfluencerDashboard";
import EventsSponsors from "./pages/EventsSponsors";
import EventDetail from "./pages/EventDetail";
import SponsorEvent from "./pages/SponsorEvent";
import EventListingSignUp from "./pages/EventListingSignUp";
import ClientDashboard from "./pages/ClientDashboard";
import { AuthProvider } from "./contexts/AuthContext";
import { ProtectedRoute } from "./components/ProtectedRoute";
import Auth from "./pages/Auth";
import ResetPassword from "./pages/ResetPassword";
import AdminDashboard from "./pages/AdminDashboard";
import AdminPerformance from "./pages/AdminPerformance";
import AffiliateSignUp from "./pages/AffiliateSignUp";
import AffiliateDashboard from "./pages/AffiliateDashboard";
import MediaOwnerDashboard from "./pages/MediaOwnerDashboard";
import AffiliateProgram from "./pages/AffiliateProgram";

const RoutedApp = () => {
  const location = useLocation();
  useEffect(() => {
    window.scrollTo({ top: 0, left: 0 });
  }, [location.pathname]);
  return (
    <div key={location.pathname} className="page-transition">
      <Routes>
        <Route path="/" element={<Home />} />
        <Route path="/partners" element={<Partners />} />
        <Route path="/partners/:partnerId" element={<PartnerDetail />} />
        <Route path="/campaign" element={<ProtectedRoute><CampaignPage /></ProtectedRoute>} />
        <Route path="/campaign/choice" element={<ProtectedRoute><CampaignChoicePage /></ProtectedRoute>} />
        <Route path="/campaign/ai-chat" element={<ProtectedRoute><AICampaignChatPage /></ProtectedRoute>} />
        <Route path="/ai-campaign-creator" element={<AICampaignCreator />} />
        <Route path="/campaign/create" element={<ProtectedRoute><CampaignPage mode="create" /></ProtectedRoute>} />
        <Route path="/campaign/edit/:campaignId" element={<ProtectedRoute><CampaignPage mode="edit" /></ProtectedRoute>} />
        <Route path="/campaign/draft/:draftId" element={<ProtectedRoute><CampaignPage mode="draft" /></ProtectedRoute>} />
        <Route path="/campaign/payment/callback" element={<ProtectedRoute><PaymentCallback /></ProtectedRoute>} />
        <Route path="/faqs" element={<FAQs />} />
        <Route path="/privacy-policy" element={<PrivacyPolicy />} />
        <Route path="/return-policy" element={<ReturnPolicy />} />
        <Route path="/contact" element={<Contact />} />
        <Route path="/who-we-are" element={<WhoWeAre />} />
        <Route path="/influencers" element={<Influencers />} />
        <Route path="/influencer-booking" element={<InfluencerBooking />} />
        <Route path="/influencers/:influencerId" element={<InfluencerDetail />} />
        <Route path="/influencers/:influencerId/book" element={<BookInfluencer />} />
        <Route path="/influencer-signup" element={<ProtectedRoute><InfluencerSignUp /></ProtectedRoute>} />
        <Route path="/influencer-dashboard" element={<ProtectedRoute><InfluencerDashboard /></ProtectedRoute>} />
        <Route path="/events-sponsors" element={<EventsSponsors />} />
        <Route path="/events/:eventId" element={<EventDetail />} />
        <Route path="/events/:eventId/sponsor" element={<SponsorEvent />} />
        <Route path="/event-listing-signup" element={<ProtectedRoute><EventListingSignUp /></ProtectedRoute>} />
        <Route path="/client-dashboard" element={<ClientDashboard />} />
        <Route path="/dashboard" element={<Navigate to="/" replace />} />
        <Route path="/admin-dashboard" element={<ProtectedRoute><AdminDashboard /></ProtectedRoute>} />
        <Route path="/admin/performance" element={<ProtectedRoute><AdminPerformance /></ProtectedRoute>} />
        <Route path="/affiliate-signup" element={<ProtectedRoute><AffiliateSignUp /></ProtectedRoute>} />
        <Route path="/affiliate-dashboard" element={<ProtectedRoute><AffiliateDashboard /></ProtectedRoute>} />
        <Route path="/media-owner-dashboard" element={<MediaOwnerDashboard />} />
        <Route path="/affiliate-program" element={<AffiliateProgram />} />
        <Route path="/auth" element={<Auth />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="*" element={<NotFound />} />
      </Routes>
    </div>
  );
};

const App = () => {
  return (
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <ThemeProvider>
          <AuthProvider>
            <BrowserRouter>
              <TooltipProvider>
                <Toaster />
                <Sonner />
              <LocationAwareErrorBoundary>
              <AppLayout>
                <RoutedApp />
              </AppLayout>
              </LocationAwareErrorBoundary>
            </TooltipProvider>
          </BrowserRouter>
        </AuthProvider>
        </ThemeProvider>
      </QueryClientProvider>
    </ErrorBoundary>
  );
};

export default App;
